"use client";

import React from "react";
import { Loader, ListFilterPlus, ChevronUp } from "lucide-react";
import { UploadStatus } from "..";

type SubmitButtonProps = {
  selectedFileName: string;
  isSubmitting: boolean;
  clickAnimation: boolean;
  status: UploadStatus;
  uploadProgress: number;
  handleButtonClick: () => void;
  customSubmit: (e?: React.FormEvent) => void;
  isDisabled: (state: UploadStatus["state"]) => boolean;
  isProcessing: (state: UploadStatus["state"]) => boolean;
  isSuccess: (state: UploadStatus["state"]) => boolean;
  // オプション設定関連
  onOptionsClick: () => void;
  hasOptions: boolean;
  isOptionsOpen: boolean;
};

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  selectedFileName,
  isSubmitting,
  clickAnimation,
  status,
  uploadProgress,
  handleButtonClick,
  customSubmit,
  isDisabled,
  isProcessing,
  isSuccess,
  onOptionsClick,
  hasOptions,
  isOptionsOpen
}) => {
  // アップロード中かどうかを判定
  const isUploading = selectedFileName && uploadProgress < 100;
  
  // ボタンテキスト取得
  const getButtonText = (state: UploadStatus["state"]) => {
    if (isProcessing(state)) {
      return (
        <span className="flex items-center justify-center">
          <Loader className="animate-spin mr-2 h-4 w-4" />
          処理中...
        </span>
      );
    }
    if (isSuccess(state)) {
      return "完了";
    }
    // アップロード中の場合は、アップロード中と表示
    if (isUploading) {
      return (
        <span className="flex items-center justify-center">
          <Loader className="animate-spin mr-2 h-4 w-4" />
          アップロード中...
        </span>
      );
    }
    return selectedFileName ? "構造化データを生成" : "音声ファイルを選択";
  };

  // 送信ボタンの無効化条件
  const isSubmitButtonDisabled = Boolean(!selectedFileName || isSubmitting || isDisabled(status.state) || isUploading);

  return (
    <div className="relative w-full flex items-center justify-between gap-3">
      {/* オプションボタン */}
      <div className="relative flex-shrink-0">
        <button
          type="button"
          onClick={onOptionsClick}
          className={`relative p-4 rounded-2xl border transition-all duration-200 shadow-lg ${
            hasOptions || isOptionsOpen
              ? 'bg-primary/10 border-primary text-primary hover:bg-primary/20' 
              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:border-primary/50 hover:text-primary'
          }`}
          style={{ 
            backdropFilter: 'blur(14px)', 
            WebkitBackdropFilter: 'blur(14px)',
          }}
          aria-label="オプション設定"
        >
          {isOptionsOpen ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ListFilterPlus className="h-5 w-5" />
          )}
          {/* 設定済みインジケーター（パネルが閉じているときのみ表示） */}
          {hasOptions && !isOptionsOpen && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-500 rounded-full border-2 border-white dark:border-gray-900" />
          )}
        </button>
      </div>

      {/* 送信ボタン - フレックスで残りのスペースを占有 */}
      <button
        type="submit"
        onClick={(e) => {
          e.preventDefault();
          handleButtonClick();
          customSubmit(e);
        }}
        disabled={isSubmitButtonDisabled}
        className={`flex-1 py-4 px-6 font-medium text-white transition-all duration-200 rounded-2xl shadow-lg cursor-pointer
                  ${!isSubmitButtonDisabled
                    ? 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200' 
                    : 'bg-gray-400/50 cursor-not-allowed text-gray-600 dark:text-gray-400'} 
                  ${clickAnimation ? 'scale-95' : ''}
                  disabled:bg-gray-400/50 disabled:cursor-not-allowed disabled:transform-none
                  ${isSubmitting || isProcessing(status.state) || isUploading ? 'cursor-not-allowed' : ''}`}
        style={{ 
          backdropFilter: 'blur(14px)', 
          WebkitBackdropFilter: 'blur(14px)',
        }}
        aria-label="日報を作成する"
      >
        {getButtonText(status.state)}
      </button>
    </div>
  );
};